import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  message,
  Popconfirm,
  Space,
  Card,
  Tag,
  Upload,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import api from '../services/api';
import * as XLSX from 'xlsx';

const TiposServicio = () => {
  const [tiposServicio, setTiposServicio] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [editingTipo, setEditingTipo] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchTiposServicio();
  }, []);

  const fetchTiposServicio = async () => {
    setLoading(true);
    try {
      const response = await api.get('/tipos-servicio');
      setTiposServicio(response.data);
    } catch (error) {
      message.error('Error al cargar tipos de servicio');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      const data = {
        ...values,
        usa_tarifa_aseguradora: values.usa_tarifa_aseguradora !== false,
      };
      if (editingTipo) {
        await api.put(`/tipos-servicio/${editingTipo.id}`, data);
        message.success('Tipo de servicio actualizado exitosamente');
      } else {
        await api.post('/tipos-servicio', data);
        message.success('Tipo de servicio creado exitosamente');
      }
      setModalVisible(false);
      form.resetFields();
      setEditingTipo(null);
      fetchTiposServicio();
    } catch (error) {
      message.error('Error al guardar tipo de servicio');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (record) => {
    setEditingTipo(record);
    form.setFieldsValue({
      ...record,
      usa_tarifa_aseguradora: record.usa_tarifa_aseguradora === 1 || record.usa_tarifa_aseguradora === true,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/tipos-servicio/${id}`);
      message.success('Tipo de servicio eliminado exitosamente');
      fetchTiposServicio();
    } catch (error) {
      message.error('Error al eliminar tipo de servicio');
    }
  };

  const handleFileUpload = async (file) => {
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });

          if (jsonData.length < 2) {
            message.error('El archivo Excel debe tener al menos una fila de datos (después del encabezado)');
            setUploading(false);
            return;
          }

          // Obtener encabezados (primera fila)
          const headers = jsonData[0].map(h => String(h).toUpperCase().trim());
          
          // Buscar índices de columnas
          const codigoIndex = headers.findIndex(h => h.includes('CODIGO') || h === 'CÓDIGO');
          const nombreIndex = headers.findIndex(h => h.includes('NOMBRE'));
          const descripcionIndex = headers.findIndex(h => h.includes('DESCRIPCION') || h.includes('DESCRIPCIÓN'));
          const usaTarifaIndex = headers.findIndex(h => h.includes('USA_TARIFA') || h.includes('USA TARIFA') || h.includes('TARIFA'));
          const precioIndex = headers.findIndex(h => h.includes('PRECIO'));

          if (codigoIndex === -1 || nombreIndex === -1) {
            message.error('El archivo Excel debe tener las columnas CODIGO y NOMBRE');
            setUploading(false);
            return;
          }

          // Procesar datos (desde la segunda fila)
          const tiposServicioData = [];
          const errores = [];

          for (let i = 1; i < jsonData.length; i++) {
            const row = jsonData[i];
            if (!row || row.length === 0) continue;

            const codigo = row[codigoIndex] ? String(row[codigoIndex]).trim() : '';
            const nombre = row[nombreIndex] ? String(row[nombreIndex]).trim() : '';
            const descripcion = descripcionIndex !== -1 && row[descripcionIndex] ? String(row[descripcionIndex]).trim() : '';
            const usaTarifaStr = usaTarifaIndex !== -1 && row[usaTarifaIndex] ? String(row[usaTarifaIndex]).toUpperCase().trim() : 'SI';
            const precio = precioIndex !== -1 && row[precioIndex] ? parseFloat(row[precioIndex]) : null;

            if (!codigo || !nombre) {
              errores.push(`Fila ${i + 1}: Faltan CODIGO o NOMBRE`);
              continue;
            }

            const usaTarifa = usaTarifaStr === 'SI' || usaTarifaStr === 'S' || usaTarifaStr === 'YES' || usaTarifaStr === 'Y' || usaTarifaStr === '1' || usaTarifaStr === 'TRUE';

            tiposServicioData.push({
              codigo,
              nombre,
              descripcion: descripcion || null,
              usa_tarifa_aseguradora: usaTarifa,
              precio_libre: precio || null,
              estado: 'activo'
            });
          }

          if (tiposServicioData.length === 0) {
            message.error('No se encontraron datos válidos en el archivo');
            setUploading(false);
            return;
          }

          // Enviar al backend
          const response = await api.post('/tipos-servicio/carga-masiva', { tipos_servicio: tiposServicioData });
          
          if (response.data.exitosos > 0) {
            message.success(`Se cargaron exitosamente ${response.data.exitosos} tipo(s) de servicio`);
          }
          if (response.data.errores && response.data.errores.length > 0) {
            message.warning(`Algunos registros tuvieron errores: ${response.data.errores.length}`);
            console.error('Errores:', response.data.errores);
          }
          if (errores.length > 0) {
            message.warning(`Errores de validación: ${errores.length}`);
            console.error('Errores de validación:', errores);
          }

          setUploadModalVisible(false);
          fetchTiposServicio();
        } catch (error) {
          console.error('Error al procesar archivo:', error);
          message.error('Error al procesar el archivo Excel: ' + (error.message || 'Error desconocido'));
        } finally {
          setUploading(false);
        }
      };
      reader.readAsArrayBuffer(file);
    } catch (error) {
      console.error('Error al leer archivo:', error);
      message.error('Error al leer el archivo');
      setUploading(false);
    }
    return false; // Prevenir upload automático
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Código',
      dataIndex: 'codigo',
      key: 'codigo',
    },
    {
      title: 'Nombre',
      dataIndex: 'nombre',
      key: 'nombre',
    },
    {
      title: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
    },
    {
      title: 'Usa Tarifa Aseguradora',
      dataIndex: 'usa_tarifa_aseguradora',
      key: 'usa_tarifa_aseguradora',
      render: (usa) => (
        <Tag color={usa ? 'blue' : 'orange'}>
          {usa ? 'Sí' : 'No'}
        </Tag>
      ),
    },
    {
      title: 'Precio Libre',
      dataIndex: 'precio_libre',
      key: 'precio_libre',
      render: (precio) => precio ? `S/ ${precio}` : '-',
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      key: 'estado',
      render: (estado) => {
        const colors = {
          activo: 'green',
          inactivo: 'red',
        };
        return <Tag color={colors[estado]}>{estado.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Acciones',
      key: 'acciones',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Editar
          </Button>
          <Popconfirm
            title="¿Estás seguro de eliminar este tipo de servicio?"
            onConfirm={() => handleDelete(record.id)}
            okText="Sí"
            cancelText="No"
          >
            <Button type="primary" danger icon={<DeleteOutlined />}>
              Eliminar
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        title="Gestión de Tipos de Servicio"
        extra={
          <Space>
            <Button
              type="default"
              icon={<UploadOutlined />}
              onClick={() => setUploadModalVisible(true)}
            >
              Cargar desde Excel
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingTipo(null);
                form.resetFields();
                setModalVisible(true);
              }}
            >
              Nuevo Tipo de Servicio
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={tiposServicio}
          rowKey="id"
          loading={loading}
        />
      </Card>

      <Modal
        title={editingTipo ? 'Editar Tipo de Servicio' : 'Nuevo Tipo de Servicio'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setEditingTipo(null);
        }}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="codigo"
            label="Código"
            rules={[{ required: true, message: 'Ingresa el código' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="nombre"
            label="Nombre"
            rules={[{ required: true, message: 'Ingresa el nombre' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="descripcion" label="Descripción">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item
            name="usa_tarifa_aseguradora"
            label="Usa Tarifa de Aseguradora"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch />
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.usa_tarifa_aseguradora !== currentValues.usa_tarifa_aseguradora
            }
          >
            {({ getFieldValue }) => {
              const usaTarifa = getFieldValue('usa_tarifa_aseguradora');
              if (!usaTarifa) {
                return (
                  <Form.Item
                    name="precio_libre"
                    label="Precio Libre (Opcional)"
                  >
                    <InputNumber min={0} precision={2} style={{ width: '100%' }} />
                  </Form.Item>
                );
              }
              return null;
            }}
          </Form.Item>
          <Form.Item name="estado" label="Estado" initialValue="activo">
            <Select>
              <Select.Option value="activo">Activo</Select.Option>
              <Select.Option value="inactivo">Inactivo</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={submitting} disabled={submitting}>
                {editingTipo ? 'Actualizar' : 'Crear'}
              </Button>
              <Button
                onClick={() => {
                  setModalVisible(false);
                  form.resetFields();
                  setEditingTipo(null);
                }}
              >
                Cancelar
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Cargar Tipos de Servicio desde Excel"
        open={uploadModalVisible}
        onCancel={() => setUploadModalVisible(false)}
        footer={null}
        width={600}
      >
        <div style={{ marginBottom: 16 }}>
          <p><strong>Formato del archivo Excel:</strong></p>
          <p>El archivo debe tener las siguientes columnas (en la primera fila):</p>
          <ul>
            <li><strong>CODIGO</strong> (requerido)</li>
            <li><strong>NOMBRE</strong> (requerido)</li>
            <li><strong>DESCRIPCION</strong> (opcional)</li>
            <li><strong>USA_TARIFA</strong> (SI/NO, opcional, por defecto SI)</li>
            <li><strong>PRECIO</strong> (opcional)</li>
          </ul>
          <p style={{ color: '#999', fontSize: '12px' }}>
            Ejemplo:<br />
            CODIGO | NOMBRE | DESCRIPCION | USA_TARIFA | PRECIO<br />
            TS-005 | BARRANCO GRUA | | SI |<br />
            TS-006 | BARRANCO AUXILIO | | SI |
          </p>
        </div>
        <Upload
          accept=".xlsx,.xls"
          beforeUpload={handleFileUpload}
          showUploadList={false}
          disabled={uploading}
        >
          <Button type="primary" icon={<UploadOutlined />} loading={uploading} block>
            {uploading ? 'Procesando...' : 'Seleccionar archivo Excel'}
          </Button>
        </Upload>
      </Modal>
    </div>
  );
};

export default TiposServicio;

